import React, { useState } from 'react';
import { Box, Drawer, Grid, IconButton, List, Tooltip, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlayArrow } from '@mui/icons-material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import { makeStyles } from '@mui/styles';
import { useHistory } from '../../../../provider/HistoryProvider';
import useUndoRedo from '../../../../hooks/useUndoRedo';
import HistoryListEntry from './HistoryListEntry';
import { useBuilding } from '../../../../provider/BuildingProvider/BuildingProvider';
import { useToolbarContext } from '../../../../provider/ToolbarProvider';
import { EWallMode } from '../../../../@types/Enums';
import { useSelection } from '../../../../provider/SelectionProvider';
import { immersightBlue } from '../../../../utils/colors';

interface HistoryDrawerProps {
  id: string;
  isOpen: boolean;
  onClose: () => void;
}

const HistoryDrawer: React.FunctionComponent<HistoryDrawerProps> = ({ id, isOpen, onClose }) => {
  // Local State
  const [hoverIndex, setHoverIndex] = useState<number | undefined>(undefined);

  // Hooks
  const intl = useIntl();
  const classes = useStyles();
  const { setBuilding } = useBuilding();
  const { history, currentIndex } = useHistory();
  const { undo, redo } = useUndoRedo();
  const { setWallMode } = useToolbarContext();
  const { resetSelectionWithoutHistoryDrawer: resetSelectionWithoutDrawer } = useSelection();

  const handleHistoryEntryClick = (i: number) => {
    if (i === currentIndex) return;

    if (i < (currentIndex ?? 0)) {
      resetSelectionWithoutDrawer();
      setWallMode(EWallMode.NONE);
      undo(setBuilding, i);
    } else if (i >= (currentIndex ?? 0)) {
      resetSelectionWithoutDrawer();
      setWallMode(EWallMode.NONE);
      redo(setBuilding, i);
    }
  };

  // History entries
  const entries = history
    .map((historEntry, index) => (
      <HistoryListEntry
        entry={historEntry}
        hasHoverContext={hoverIndex !== undefined && index > hoverIndex}
        isCurrent={index === currentIndex}
        isRedoStack={currentIndex !== undefined && index > currentIndex}
        key={'historyEntry-' + index}
        onEntryClick={() => handleHistoryEntryClick(index)}
        onEntryHover={() => setHoverIndex(index)}
      />
    ))
    .reverse();

  return (
    <Drawer
      key={id}
      anchor="right"
      open={isOpen}
      variant={isOpen ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          // Special case, overriding material UI class
          top: '64px',
          zIndex: 1000,
          width: '300px',
          height: 'calc(100% - 64px)',
          overflow: 'hidden',
        },
      }}
    >
      <Grid
        item
        sx={{
          backgroundColor: immersightBlue,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 0.5,
        }}
      >
        <Box sx={{ marginLeft: '16px' }}>
          <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
            <FormattedMessage id="HistoryDrawer.title" />
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}>
          <PlayArrow sx={{ fontSize: 30 }} />
        </IconButton>
      </Grid>
      <Box className={classes.historyDrawerInfo}>
        <Typography variant="body2">
          <FormattedMessage id="HistoryDrawer.info" />
        </Typography>
        <Tooltip
          PopperProps={{
            modifiers: [
              { name: 'preventOverflow', options: { boundary: 'window' } },
              { name: 'offset', options: { offset: [8, 0] } },
            ],
          }}
          title={intl.formatMessage({
            id: 'HistoryDrawer.toolbarInfo',
          })}
          placement="top-start"
          arrow
        >
          <HelpOutlineRoundedIcon />
        </Tooltip>
      </Box>
      <Box className={classes.historyDrawerContent}>
        <List>{[...entries]}</List>
      </Box>
    </Drawer>
  );
};

export default HistoryDrawer;

const useStyles = makeStyles((_) => ({
  historyDrawerHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
  },
  historyDrawerInfo: {
    padding: '8px 16px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '8px',
    userSelect: 'none',
  },
  historyDrawerContent: {
    flexGrow: 1,
    overflowY: 'scroll',
  },
}));
